import axios from 'axios';

import {
  getDisabledImageKeySet,
  getResultImageUrls,
  getResultLotNumber,
  getReviewNoteDraftKey,
  normalizeImageKey,
} from '../../utils/testRigHelpers';

export const createResultAndNotesControls = (deps) => {
  const {
    setTestResults,
    setSavingReviewNotes,
    setReviewNoteDrafts,
    setSavingLotNotes,
    setLotNoteDrafts,
    fetchData,
    mergeUpdatedRunInState,
  } = deps;

  const updateRunLotResultInState = (runId, lotNumber, updater, options = {}) => {
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    const targetResultIndex = Number.isFinite(Number(options?.resultIndex))
      ? Number(options.resultIndex)
      : null;
    if (!normalizedRunId || typeof updater !== 'function') return;
    if (!normalizedLot && targetResultIndex === null) return;

    setTestResults((previousRuns) =>
      previousRuns.map((run) => {
        if (String(run?.run_id || '').trim() !== normalizedRunId) return run;
        if (!Array.isArray(run?.results)) return run;

        let didChange = false;
        const nextResults = run.results.map((resultEntry, resultIndex) => {
          const resultLot = getResultLotNumber(resultEntry);
          const isTarget =
            targetResultIndex !== null
              ? resultIndex === targetResultIndex
              : resultLot === normalizedLot;
          if (!isTarget) return resultEntry;
          const updated = updater(resultEntry);
          if (updated !== resultEntry) {
            didChange = true;
            return updated;
          }
          return resultEntry;
        });

        if (!didChange) return run;
        return { ...run, results: nextResults };
      }),
    );
  };

  const getLotResultEntry = (runs, runId, lotNumber, resultIndex = null) => {
    if (!Array.isArray(runs)) return null;
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    const normalizedResultIndex = Number.isFinite(Number(resultIndex))
      ? Number(resultIndex)
      : null;
    if (!normalizedRunId) return null;

    const runEntry = runs.find(
      (entry) => String(entry?.run_id || '').trim() === normalizedRunId,
    );
    if (!runEntry || !Array.isArray(runEntry.results)) return null;

    if (normalizedResultIndex !== null) {
      return runEntry.results[normalizedResultIndex] || null;
    }
    if (!normalizedLot) return null;
    return (
      runEntry.results.find(
        (resultEntry) =>
          String(getResultLotNumber(resultEntry) || '').trim() === normalizedLot,
      ) || null
    );
  };

  const normalizeImageKeyList = (values) =>
    Array.from(
      new Set(
        (Array.isArray(values) ? values : [])
          .map((value) => normalizeImageKey(value))
          .filter(Boolean),
      ),
    ).sort();

  const isExpectedLastImageToggleBlock = (message) =>
    String(message || '')
      .toLowerCase()
      .includes('at least one image must stay enabled');

  const getEnabledImageKeyCountForResult = (resultEntry) => {
    const imageUrls = getResultImageUrls(resultEntry);
    const disabledKeySet = getDisabledImageKeySet(resultEntry);
    const enabledKeys = new Set();
    imageUrls.forEach((url) => {
      const imageKey = normalizeImageKey(url) || String(url || '').trim().toLowerCase();
      if (!imageKey || disabledKeySet.has(imageKey)) return;
      enabledKeys.add(imageKey);
    });
    return enabledKeys.size;
  };

  const saveReviewNote = async ({
    runId,
    lotNumber,
    resultIndex,
    itemIndex,
    historyIndex,
    reviewNote,
  }) => {
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    const normalizedResultIndex = Number(resultIndex);
    const normalizedItemIndex = Number(itemIndex);
    const normalizedHistoryIndex = historyIndex != null ? Number(historyIndex) : null;

    if (!normalizedRunId || !normalizedLot) {
      alert('Missing run_id or lot_number for review note update.');
      return;
    }
    if (!Number.isFinite(normalizedResultIndex) || normalizedResultIndex < 0) {
      alert('Missing valid result index for review note update.');
      return;
    }
    if (!Number.isFinite(normalizedItemIndex) || normalizedItemIndex < 0) {
      alert('Missing valid item index for review note update.');
      return;
    }

    const noteDraftKey = getReviewNoteDraftKey({
      runId: normalizedRunId,
      lotNumber: normalizedLot,
      resultIndex: normalizedResultIndex,
      itemIndex: normalizedItemIndex,
      historyIndex: normalizedHistoryIndex,
    });
    const noteText = String(reviewNote || '').slice(0, 4000);

    setSavingReviewNotes((previous) => ({ ...previous, [noteDraftKey]: true }));
    try {
      const payload = {
        run_id: normalizedRunId,
        lot_number: normalizedLot,
        result_index: normalizedResultIndex,
        item_index: normalizedItemIndex,
        review_note: noteText,
      };
      if (normalizedHistoryIndex != null) {
        payload.history_index = normalizedHistoryIndex;
      }
      const response = await axios.post('/test_rig_update_item_note', payload);
      if (response?.data?.status === 'success') {
        const returnedRun = response?.data?.run;
        const returnedResult = response?.data?.result;
        if (returnedRun && typeof returnedRun === 'object') {
          mergeUpdatedRunInState(returnedRun);
        } else if (returnedResult && typeof returnedResult === 'object') {
          updateRunLotResultInState(normalizedRunId, normalizedLot, () => returnedResult);
        } else {
          fetchData({ showLoading: false });
        }
        setReviewNoteDrafts((previous) => {
          const next = { ...previous };
          delete next[noteDraftKey];
          return next;
        });
      } else {
        alert(response?.data?.message || 'Failed to save reviewer note.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to save reviewer note.';
      alert(message);
    } finally {
      setSavingReviewNotes((previous) => {
        const next = { ...previous };
        delete next[noteDraftKey];
        return next;
      });
    }
  };

  const saveLotReviewNote = async ({
    runId,
    lotNumber,
    resultIndex,
    historyIndex,
    reviewNote,
  }) => {
    const normalizedRunId = String(runId || '').trim();
    const normalizedLot = String(lotNumber || '').trim();
    const normalizedResultIndex = Number(resultIndex);
    const normalizedHistoryIndex = historyIndex != null ? Number(historyIndex) : null;

    if (!normalizedRunId || !normalizedLot) {
      alert('Missing run_id or lot_number for lot note update.');
      return;
    }
    if (!Number.isFinite(normalizedResultIndex) || normalizedResultIndex < 0) {
      alert('Missing valid result index for lot note update.');
      return;
    }

    const draftKey = `lot-${normalizedRunId}-${normalizedLot}-${normalizedResultIndex}${
      normalizedHistoryIndex != null ? `-h${normalizedHistoryIndex}` : ''
    }`;
    const noteText = String(reviewNote || '').slice(0, 4000);
    setSavingLotNotes((prev) => ({ ...prev, [draftKey]: true }));

    try {
      const payload = {
        run_id: normalizedRunId,
        lot_number: normalizedLot,
        result_index: normalizedResultIndex,
        review_note: noteText,
      };
      if (normalizedHistoryIndex != null) {
        payload.history_index = normalizedHistoryIndex;
      }
      const response = await axios.post('/test_rig_update_lot_note', payload);

      if (response?.data?.status === 'success') {
        const returnedRun = response?.data?.run;
        const returnedResult = response?.data?.result;
        if (returnedRun && typeof returnedRun === 'object') {
          mergeUpdatedRunInState(returnedRun);
        } else if (returnedResult && typeof returnedResult === 'object') {
          updateRunLotResultInState(normalizedRunId, normalizedLot, () => returnedResult);
        } else {
          fetchData({ showLoading: false });
        }
        setLotNoteDrafts((prev) => {
          const next = { ...prev };
          delete next[draftKey];
          return next;
        });
      } else {
        alert(response?.data?.message || 'Failed to save lot note.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to save lot note.';
      alert(message);
    } finally {
      setSavingLotNotes((prev) => {
        const next = { ...prev };
        delete next[draftKey];
        return next;
      });
    }
  };

  return {
    updateRunLotResultInState,
    getLotResultEntry,
    normalizeImageKeyList,
    isExpectedLastImageToggleBlock,
    getEnabledImageKeyCountForResult,
    saveReviewNote,
    saveLotReviewNote,
  };
};
